import * as React from "react"
import { BookingOutcomeWarning } from "./BookingOutcomeWarning"
import { bookingOutcomeWarning } from "../../shared/bookingOutcome"
import { ADMIN_TABS, navigationFromUrl, navigationUrl, type Tab, type AnalyticsView, type SettingsView } from "./adminNavigation"
import { Settings } from "./Settings"
import { EquipmentStack } from "./EquipmentStack"
import { PostcodeMapDialog } from "./PostcodeMapDialog"
import { Operations, OperationsSummary } from "./Operations"
import { OnlineBookingDialog } from './OnlineBookingDialog'
import { ProductAssignmentSettings } from "./ProductAssignmentSettings"
import { ProductDeliveryTariffs } from "./ProductDeliveryTariffs"
const Analytics = React.lazy(() => import("./Analytics").then(module => ({ default: module.Analytics })))
import { Button, AdminField, Switch, Badge, Dialog, AstryxTable, TableHeader, TableBody, TableRow, TableCell, TableHeaderCell } from "./ui"
import type { Session } from "@supabase/supabase-js"
import {
  MAX_DELIVERY_AREA_IMPORT_BYTES,
  parseDeliveryAreaCsv,
  parseDeliveryFeeGbp,
  type DeliveryAreaImportRule,
} from "./deliveryAreaCsv"
import { bookingApiUrl, supabase } from "./supabase"
import { DateSettings } from "./DateSettings"
import { LiveUsersMetric } from "./LiveUsersMetric"
import { TodaysBookingsMetric } from "./TodaysBookingsMetric"
import { DashboardVisitorCharts } from "./DashboardVisitorCharts"
import { addCalendarDays, londonToday } from "./dateSettingsCalendar"
import { BookingProductSummary, productGroupLabel } from "./ProductSummary"

type Message = { tone: "error" | "success"; text: string } | null
type AdminApproval = { userId: string; active: boolean; canWrite: boolean }

type AvailabilityCheck = {
  id: number
  postcode: string
  outward_code: string
  delivery_date: string | null
  collection_date: string | null
  quantity: number
  outcome: "available" | "unavailable" | "contact_required"
  reason_code: string | null
  available_quantity: number
  delivery_fee_pence: number | null
  hire_pence?: number | null
  free_delivery: boolean | null
  first_checked_at: string
  last_checked_at: string
  check_count: number
  product_name: string | null
  product_group?: string | null
  product_sku?: string | null
  hire_days?: number
  led_to_booking: boolean
}

type AvailabilityCheckGroup = {
  postcode: string
  outward_code: string
  record_count: number
  check_count: number
  last_checked_at: string
  checks: AvailabilityCheck[]
}

const ADMIN_PAGE_SIZE = 10

const money = new Intl.NumberFormat("en-GB", { style: "currency", currency: "GBP" })
const date = new Intl.DateTimeFormat("en-GB", { day: "numeric", month: "short", year: "numeric" })
const dateTime = new Intl.DateTimeFormat("en-GB", {
  dateStyle: "medium",
  timeStyle: "short",
  timeZone: "Europe/London",
})
const timeOnly = new Intl.DateTimeFormat("en-GB", {
  hour: "2-digit",
  minute: "2-digit",
  timeZone: "Europe/London",
})
const dateHeading = new Intl.DateTimeFormat("en-GB", {
  weekday: "long",
  day: "numeric",
  month: "long",
  year: "numeric",
  timeZone: "Europe/London",
})

function dateOnly(value: string) {
  const [year, month, day] = value.split("-").map(Number)
  return date.format(new Date(year, month - 1, day))
}

function errorText(value: unknown) {
  return value instanceof Error ? value.message : "The action could not be completed."
}

function tabTitle(value: Tab) {
  if (value === "fleet") return "Fleet calendar"
  return title(value)
}

function pageTitle(value: Tab) {
  if (value === "dashboard") return "Arctic Hire Admin"
  return tabTitle(value)
}

function londonDateKey(value: string): string {
  const parts = new Intl.DateTimeFormat("en-GB", {
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
    timeZone: "Europe/London",
  }).formatToParts(new Date(value))
  const part = (type: Intl.DateTimeFormatPartTypes) => parts.find((item) => item.type === type)?.value ?? ""
  return `${part("year")}-${part("month")}-${part("day")}`
}

function bookedAtLabel(value: string, today: string) {
  const bookedDay = londonDateKey(value)
  const yesterday = new Date(Date.parse(today) - 86_400_000).toISOString().slice(0, 10)
  const relativeDay = bookedDay === today ? "Today" : bookedDay === yesterday ? "Yesterday" : null
  return relativeDay ? `${relativeDay}, ${timeOnly.format(new Date(value))}` : dateTime.format(new Date(value))
}

function groupByCreatedDate<T>(items: T[], timestamp: (item: T) => string) {
  const today = londonToday()
  const yesterday = addCalendarDays(today, -1)
  const groups: Array<{ key: string; label: string; items: T[] }> = []
  for (const item of items) {
    const value = timestamp(item)
    const key = londonDateKey(value)
    const existing = groups.find((group) => group.key === key)
    if (existing) existing.items.push(item)
    else groups.push({ key, label: key === today ? "Today" : key === yesterday ? "Yesterday" : dateHeading.format(new Date(value)), items: [item] })
  }
  return groups
}

export function App() {
  const [session, setSession] = React.useState<Session | null>(null)
  const [authLoading, setAuthLoading] = React.useState(true)
  const [approval, setApproval] = React.useState<AdminApproval | null>(null)
  const [tab, setTab] = React.useState<Tab>(() => navigationFromUrl(window.location.href).tab)
  const [analyticsView, setAnalyticsView] = React.useState<AnalyticsView>(() => navigationFromUrl(window.location.href).analyticsView)
  const [settingsView, setSettingsView] = React.useState<SettingsView>(() => navigationFromUrl(window.location.href).settingsView)
  const [visitedTabs, setVisitedTabs] = React.useState<Set<Tab>>(() => new Set([tab]))
  const [inventoryRevision, setInventoryRevision] = React.useState(0)
  const [inventorySearch, setInventorySearch] = React.useState("")
  const [assignmentRevision, setAssignmentRevision] = React.useState(0)
  const [onlineBookingId, setOnlineBookingId] = React.useState(() => new URLSearchParams(window.location.search).get("booking") || "")
  const [message, setMessage] = React.useState<Message>(null)

  React.useEffect(() => {
    document.title = pageTitle(tab)
  }, [tab])

  React.useEffect(() => {
    supabase.auth.getSession().then(({ data }) => setSession(data.session)).finally(() => setAuthLoading(false))
    const { data } = supabase.auth.onAuthStateChange((_event, next) => setSession(next))
    return () => data.subscription.unsubscribe()
  }, [])

  React.useEffect(() => {
    const userId = session?.user.id
    if (!userId) {
      setApproval(null)
      return
    }
    let active = true
    Promise.resolve(supabase.from("admin_profiles").select("active,role").eq("user_id", userId).eq("active", true).maybeSingle())
      .then(({ data, error }) => {
        if (!active) return
        if (error) setMessage({ tone: "error", text: error.message })
        setApproval({ userId, active: Boolean(data?.active), canWrite: data?.role === "admin" })
      })
    return () => { active = false }
  }, [session?.user.id])

  React.useEffect(() => {
    const syncFromUrl = () => {
      const next = navigationFromUrl(window.location.href)
      const nextTab = next.tab
      if (nextTab === "analytics") setAnalyticsView(next.analyticsView)
      if (nextTab === "settings") setSettingsView(next.settingsView)
      setVisitedTabs((current) => current.has(nextTab) ? current : new Set([...current, nextTab]))
      setTab(nextTab)
      setOnlineBookingId(new URL(window.location.href).searchParams.get("booking") || "")
      setMessage(null)
    }
    const url = navigationUrl(window.location.href, { tab, analyticsView, settingsView })
    if (url.href !== window.location.href) {
      window.history.replaceState({}, "", url)
    }
    window.addEventListener("popstate", syncFromUrl)
    return () => window.removeEventListener("popstate", syncFromUrl)
  }, [])

  const changeTab = React.useCallback((nextTab: Tab, views: { analyticsView?: AnalyticsView; settingsView?: SettingsView } = {}) => {
    const nextAnalyticsView = views.analyticsView ?? analyticsView
    const nextSettingsView = views.settingsView ?? settingsView
    setAnalyticsView(nextAnalyticsView)
    setSettingsView(nextSettingsView)
    setVisitedTabs((current) => current.has(nextTab) ? current : new Set([...current, nextTab]))
    setTab(nextTab)
    setMessage(null)
    const url = navigationUrl(window.location.href, { tab: nextTab, analyticsView: nextAnalyticsView, settingsView: nextSettingsView })
    url.searchParams.delete("booking")
    setOnlineBookingId("")
    if (nextTab === tab && nextAnalyticsView === analyticsView && nextSettingsView === settingsView) window.history.replaceState({}, "", url)
    else window.history.pushState({}, "", url)
  }, [tab, analyticsView, settingsView])

  if (authLoading) return <FullScreenLoading />
  if (!session) return <Login />
  if (!approval || approval.userId !== session.user.id) return <FullScreenLoading />
  if (!approval.active) return <NotApproved email={session.user.email ?? "this account"} />

  const openOnline = (id: string) => {
    setOnlineBookingId(id)
    const url = new URL(window.location.href)
    url.searchParams.set("booking", id)
    window.history.pushState({}, "", url)
  }
  const closeOnline = () => {
    setOnlineBookingId("")
    const url = new URL(window.location.href)
    url.searchParams.delete("booking")
    window.history.replaceState({}, "", url)
  }
  const pages: Record<Tab, React.ReactNode> = {
    analytics: <React.Suspense fallback={<SectionLoading />}><Analytics active={tab === "analytics"} selectedView={analyticsView} onViewChange={view => changeTab("analytics", { analyticsView: view })} postcodeChecks={<PostcodeChecks />} /></React.Suspense>,
    dashboard: <div className="page-stack"><Dashboard refreshKey={assignmentRevision} active={tab === "dashboard"} onChecks={() => changeTab("analytics", { analyticsView: "checks" })} onOpen={openOnline} /><OperationsSummary active={tab === "dashboard"} onBookings={() => changeTab("bookings")} /></div>,
    inventory: <div className="page-stack"><Inventory search={inventorySearch} onSearch={setInventorySearch} canWrite={approval.canWrite} setMessage={setMessage} onChanged={() => setInventoryRevision(value => value + 1)} /><Operations inventorySearch={inventorySearch} refreshKey={assignmentRevision + inventoryRevision} mode="inventory" active={tab === "inventory"} canWrite={approval.canWrite} onOnline={openOnline} /></div>,
    settings: <Settings view={settingsView} onViewChange={view => changeTab("settings", { settingsView: view })} pages={{
      dates: <DateSettings active={tab === "settings" && settingsView === "dates"} canWrite={approval.canWrite} />,
      products: <div className="page-stack"><ProductAssignmentSettings canWrite={approval.canWrite} /><Products setMessage={setMessage} /></div>,
      areas: <div className="page-stack"><ProductDeliveryTariffs canWrite={approval.canWrite} /><DeliveryAreas setMessage={setMessage} /></div>,
    }} />,
    bookings: <Operations refreshKey={assignmentRevision} mode="bookings" active={tab === "bookings"} canWrite={approval.canWrite} onOnline={openOnline} onAssigned={() => setAssignmentRevision(value => value + 1)} />,
    customers: <Operations refreshKey={assignmentRevision} mode="customers" active={tab === "customers"} canWrite={approval.canWrite} onOnline={openOnline} />,
    fleet: <Operations refreshKey={assignmentRevision} mode="fleet" active={tab === "fleet"} canWrite={approval.canWrite} onOnline={openOnline} />,
  }

  return (
    <div className="app-shell">
      <aside className="sidebar">
        <div className="brand"><img className="brand-logo" src="/arctic-hire-logo.png" alt="Arctic Hire" width="6000" height="1800" /><button type="button" className="brand-calendar" aria-label="Open fleet calendar" aria-current={tab === "fleet" ? "page" : undefined} title="Fleet calendar" onClick={() => changeTab("fleet")}><svg width="19" height="19" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"><rect x="3" y="5" width="18" height="16" rx="3" /><path d="M16 3v4M8 3v4M3 11h18M8 15h2M14 15h2M8 18h2" /></svg></button></div>
        <nav aria-label="Admin sections">
          {ADMIN_TABS.filter(item => item !== "fleet").map((item) => (
            <Button type="button" key={item} icon={<NavIcon name={item} />} className={tab === item ? "active" : ""} aria-current={tab === item ? "page" : undefined} onClick={() => changeTab(item)}>
              {tabTitle(item)}
            </Button>
          ))}
        </nav>
        <div className="sidebar-user">
          <small>Signed in as</small><span>{session.user.email}</span>
          <Button type="button" onClick={() => supabase.auth.signOut()}>Sign out</Button>
        </div>
      </aside>
      <main className="content">
        <header className={`topbar${(tab === "fleet" || tab === "bookings" || tab === "customers" || tab === "inventory" || tab === "analytics") ? " topbar-utility" : ""}`}>
          {tab !== "fleet" && tab !== "bookings" && tab !== "customers" && tab !== "inventory" && tab !== "analytics" && <h1>{tabTitle(tab)}</h1>}
          <Button type="button" className="mobile-signout" onClick={() => supabase.auth.signOut()}>Sign out</Button>
        </header>
        <div className="mobile-nav">
          <select value={tab} onChange={(event) => changeTab(event.target.value as Tab)} aria-label="Admin section">
            {ADMIN_TABS.map((item) => <option key={item} value={item}>{tabTitle(item)}</option>)}
          </select>
        </div>
        {message && <div className={`message ${message.tone}`} role="status">{message.text}</div>}
        {ADMIN_TABS.map((item) => visitedTabs.has(item) && (
          <div key={item} hidden={tab !== item} aria-hidden={tab !== item}>
            {pages[item]}
          </div>
        ))}
        {onlineBookingId && <OnlineBookingDialog key={onlineBookingId} bookingId={onlineBookingId} canWrite={approval.canWrite} onClose={closeOnline} onChanged={() => setAssignmentRevision(value => value + 1)} />}
      </main>
    </div>
  )
}

function Login() {
  const [email, setEmail] = React.useState("")
  const [sent, setSent] = React.useState(false)
  const [error, setError] = React.useState("")
  const [busy, setBusy] = React.useState(false)
  const submit = async (event: React.FormEvent) => {
    event.preventDefault(); setBusy(true); setError("")
    const { error: authError } = await supabase.auth.signInWithOtp({
      email,
      options: { emailRedirectTo: window.location.origin },
    })
    setBusy(false)
    if (authError) setError(authError.message)
    else setSent(true)
  }
  return <div className="auth-page"><form className="auth-card" onSubmit={submit}>
    <div className="auth-mark">AH</div><small>ARCTIC HIRE</small><h1>Booking admin</h1>
    {sent ? <><h2>Check your email</h2><p>A secure sign-in link was sent to {email}.</p><Button type="button" className="secondary" onClick={() => setSent(false)}>Use another email</Button></> : <>
      <p>Use the approved administrator email address.</p>
      <AdminField>Email address<input type="email" autoComplete="email" value={email} onChange={(event) => setEmail(event.target.value)} required /></AdminField>
      {error && <div className="message error">{error}</div>}
      <Button className="primary" disabled={busy}>{busy ? "Sending…" : "Email me a sign-in link"}</Button>
    </>}
  </form></div>
}

function NotApproved({ email }: { email: string }) {
  return <div className="auth-page"><div className="auth-card"><div className="auth-mark">!</div><h1>Access not approved</h1><p>{email} is signed in but does not have an active Arctic Hire admin profile.</p><Button className="secondary" onClick={() => supabase.auth.signOut()}>Sign out</Button></div></div>
}

function FullScreenLoading() { return <div className="loading-page"><span /><strong>Loading Arctic Hire…</strong></div> }

function useLoad<T>(loader: () => PromiseLike<{ data: T | null; error: { message: string } | null }>, dependencies: React.DependencyList = []) {
  const [data, setData] = React.useState<T | null>(null)
  const [error, setError] = React.useState("")
  const [loading, setLoading] = React.useState(true)
  const [version, setVersion] = React.useState(0)
  React.useEffect(() => {
    let active = true; setLoading(true)
    Promise.resolve(loader()).then((result) => {
      if (!active) return
      if (result.error) setError(result.error.message)
      else { setData(result.data); setError("") }
    }).finally(() => active && setLoading(false))
    return () => { active = false }
  }, [...dependencies, version])
  return { data, error, loading, reload: () => setVersion((value) => value + 1) }
}

function usePagedLoad<T>(
  loader: () => PromiseLike<{ data: T | null; count: number | null; error: { message: string } | null }>,
  dependencies: React.DependencyList,
) {
  const [data, setData] = React.useState<T | null>(null)
  const [total, setTotal] = React.useState(0)
  const [error, setError] = React.useState("")
  const [loading, setLoading] = React.useState(true)
  const [version, setVersion] = React.useState(0)
  React.useEffect(() => {
    let active = true
    setLoading(true)
    Promise.resolve(loader()).then((result) => {
      if (!active) return
      if (result.error) setError(result.error.message)
      else {
        setData(result.data)
        setTotal(result.count ?? 0)
        setError("")
      }
    }).finally(() => active && setLoading(false))
    return () => { active = false }
  }, [...dependencies, version])
  return { data, total, error, loading, reload: () => setVersion((value) => value + 1) }
}

async function deleteBooking(bookingId: string): Promise<void> {
  const { data, error } = await supabase.auth.getSession()
  if (error || !data.session) throw new Error("Sign in to the admin again and retry.")
  const response = await fetch(`${bookingApiUrl}/v1/admin/bookings/delete`, {
    method: "POST",
    headers: {
      Authorization: `Bearer ${data.session.access_token}`,
      "Content-Type": "application/json",
    },
    body: JSON.stringify({ bookingId }),
  })
  const payload = await response.json().catch(() => ({}))
  if (!response.ok) throw new Error(payload?.error?.message || "The booking could not be deleted.")
}

function Dashboard({ active, onOpen, onChecks, refreshKey }: { refreshKey: number; active: boolean; onOpen: (id: string) => void; onChecks: () => void }) {
  const today = londonDateKey(new Date().toISOString())
  const [page, setPage] = React.useState(1)
  const from = (page - 1) * ADMIN_PAGE_SIZE
  const { data: bookings, total, loading, error } = usePagedLoad<any[]>(() => supabase.from("bookings")
    .select("id,created_at,customer_name,delivery_address,postcode,state,payment_state,delivery_date,operational_delivery_date,hire_start_date,hire_end_date,operational_hire_end_date,collection_date,operational_collection_date,quantity,booking_reference,line_items,products(name,product_group,sku)", { count: "exact" })
    .in("state", ["confirmed", "unassigned"])
    .or(`operational_collection_date.gte.${today},and(operational_collection_date.is.null,collection_date.gte.${today})`)
    .order("created_at", { ascending: false })
    .range(from, from + ADMIN_PAGE_SIZE - 1), [page, today, refreshKey])
  const { data: todayChecks } = useLoad<number>(() => supabase.rpc("admin_today_availability_check_count"))
  if (loading) return <SectionLoading />
  if (error) return <Empty title="Dashboard could not load" body={error} />
  const rows = bookings ?? []
  return <div className="page-stack">
    <div className="dashboard-overview">
      <div className="dashboard-metrics">
        <TodaysBookingsMetric active={active} refreshKey={refreshKey} />
        <article className="metric"><small>Today's checks</small><button type="button" className="metric-link" aria-label={`View today’s ${todayChecks ?? 0} postcode checks`} onClick={onChecks}><strong>{todayChecks ?? 0}</strong></button></article>
        <LiveUsersMetric active={active} />
      </div>
      <DashboardVisitorCharts active={active} />
    </div>
    <Panel title="Upcoming bookings" subtitle={`${total} confirmed booking${total === 1 ? "" : "s"} still to complete`}>
      <Table className="dashboard-bookings-table" onRowClick={index => onOpen(rows[index].id)} headers={["", "Booked at", "Customer", "Address", "Units", "Delivery / collection / duration", "Qty", "Status"]} rows={rows.map((item) => {
        const address = item.delivery_address
        const addressLines = typeof address === "string" ? [address] : [address?.line1, address?.line2, address?.city, address?.state]
        const postcode = (typeof address === "object" && address && (address.postal_code || address.postcode)) || item.postcode
        const addressText = addressLines.filter(Boolean).join("\n")
        const duration = Math.round((Date.parse(item.operational_hire_end_date || item.hire_end_date) - Date.parse(item.operational_delivery_date || item.hire_start_date)) / 86_400_000) + 1
        return [
          <EquipmentStack productType={item.products?.product_group} quantity={item.quantity} />,
          <time dateTime={item.created_at}>{bookedAtLabel(item.created_at, today)}</time>,
          <button type="button" className="bookings-customer" aria-label={`View booking ${item.booking_reference}`} onClick={() => onOpen(item.id)}>{item.customer_name || "Awaiting customer"}</button>,
          <div><span className="booking-preserve-lines">{addressText || "Not recorded"}</span>{postcode && !addressText.toLowerCase().replace(/\s/g, "").includes(postcode.toLowerCase().replace(/\s/g, "")) && <small>{postcode}</small>}</div>,
          <BookingProductSummary product={item.products} lineItems={item.line_items} />,
          <div><div>Delivery: {dateOnly(item.operational_delivery_date || item.delivery_date)}</div><div>Collection: {dateOnly(item.operational_collection_date || item.collection_date)}</div><small>{duration} {duration === 1 ? "day" : "days"} hire</small></div>,
          item.quantity,
          bookingOutcomeWarning(item.state, item.payment_state) ? <BookingOutcomeWarning state={item.state} payment={item.payment_state} /> : <Status value={item.state} />,
        ]
      })} />
      <Pagination page={page} total={total} pageSize={ADMIN_PAGE_SIZE} label="confirmed bookings" onChange={setPage} />
    </Panel>
  </div>
}

function DeleteBookingDialog({ booking, busy, onCancel, onConfirm }: { booking: any; busy: boolean; onCancel: () => void; onConfirm: () => void }) {
  const cancelButton = React.useRef<HTMLButtonElement>(null)
  React.useEffect(() => {
    cancelButton.current?.focus()
    const closeOnEscape = (event: KeyboardEvent) => { if (event.key === "Escape" && !busy) onCancel() }
    window.addEventListener("keydown", closeOnEscape)
    return () => window.removeEventListener("keydown", closeOnEscape)
  }, [busy, onCancel])
  return <Dialog isOpen onOpenChange={open => { if (!open && !busy) onCancel() }} purpose={busy ? "required" : "info"} width={480} aria-labelledby="delete-booking-title" aria-describedby="delete-booking-description">
    <section className="confirm-dialog">
      <div className="dialog-icon"><BinIcon /></div>
      <div><small>PERMANENT ACTION</small><h2 id="delete-booking-title">Delete {booking.booking_reference}?</h2></div>
      <p id="delete-booking-description">This permanently removes the booking, customer details, quote, assigned inventory, delivery reservations, notes and refund records.</p>
      <dl><div><dt>Product</dt><dd>{booking.products?.name || "—"}</dd></div><div><dt>Dates</dt><dd>{dateOnly(booking.operational_delivery_date || booking.delivery_date)} – {dateOnly(booking.operational_collection_date || booking.collection_date)}</dd></div></dl>
      <p className="dialog-note">Stripe’s financial record and the non-sensitive admin deletion audit are retained. This action cannot be undone.</p>
      <div className="dialog-actions"><Button ref={cancelButton} type="button" className="secondary" disabled={busy} onClick={onCancel}>Keep booking</Button><Button type="button" className="danger danger-solid" disabled={busy} onClick={onConfirm}>{busy ? "Deleting…" : "Delete booking"}</Button></div>
    </section>
  </Dialog>
}

function Products({ setMessage }: { setMessage: (value: Message) => void }) {
  const { data, loading, error, reload } = useLoad<any[]>(() => supabase.from("products").select("*,product_pricing_versions(pricing_config,version)").order("display_order").order("name"))
  const save = async (product: any, values: Record<string, unknown>) => {
    setMessage(null)
    const { error: rpcError } = await supabase.rpc("admin_update_product_settings", {
      p_product_id: product.id,
      p_active: values.active,
      p_one_day_enabled: values.one_day_enabled,
      p_weekend_enabled: values.weekend_enabled,
      p_minimum_notice_hours: values.minimum_notice_hours,
      p_turnaround_days: values.turnaround_days,
    })
    if (rpcError) setMessage({ tone: "error", text: rpcError.message })
    else { setMessage({ tone: "success", text: `${product.name} updated.` }); reload() }
  }
  if (loading) return <SectionLoading />
  if (error) return <Empty title="Products could not load" body={error} />
  return <div className="card-grid">{(data ?? []).map((product) => <ProductCard key={product.id} product={product} save={save} />)}</div>
}

function ProductCard({ product, save }: { product: any; save: (product: any, values: Record<string, unknown>) => void }) {
  const [values, setValues] = React.useState({
    active: product.active, one_day_enabled: product.one_day_enabled,
    weekend_enabled: product.weekend_enabled, minimum_notice_hours: product.minimum_notice_hours,
    turnaround_days: product.turnaround_days,
  })
  const pricing = product.product_pricing_versions?.find((item: any) => item.version === product.current_pricing_version)?.pricing_config
  return <article className="panel product-card">
    <div className="panel-heading"><div><small>{productGroupLabel(product.product_group)}{product.sku ? ` · SKU ${product.sku}` : ""}</small><h2>{product.name}</h2></div><Status value={values.active ? "active" : "inactive"} /></div>
    <div className="switch-grid">
      <Toggle label="Product active" checked={values.active} onChange={(active) => setValues({ ...values, active })} />
      {pricing?.model !== "daily_weekly_inclusive" && <Toggle label="Short hire rate (up to 1 hire day)" checked={values.one_day_enabled} onChange={(one_day_enabled) => setValues({ ...values, one_day_enabled })} />}
      {pricing?.model !== "daily_weekly_inclusive" && <Toggle label="Two-day rate (2 hire days)" checked={values.weekend_enabled} onChange={(weekend_enabled) => setValues({ ...values, weekend_enabled })} />}
    </div>
    <div className="form-grid">
      <AdminField>Turnaround days (automatic assignment)<input type="number" min="0" max="30" value={values.turnaround_days} onChange={(event) => setValues({ ...values, turnaround_days: Number(event.target.value) })} /></AdminField>
    </div>
    <div className="readonly-pricing"><strong>Pricing (read only)</strong><code>{pricing ? JSON.stringify(pricing, null, 2) : "Not configured"}</code></div>
    <Button className="primary" onClick={() => save(product, values)}>Save product</Button>
  </article>
}

function Inventory({ search, onSearch, canWrite, setMessage, onChanged }: { search: string; onSearch: (value: string) => void; canWrite: boolean; setMessage: (value: Message) => void; onChanged: () => void }) {
  const [adding, setAdding] = React.useState(false)
  return <>
    <header className="bookings-header inventory-header">
      <h1>Inventory</h1>
      <div className="bookings-header-actions">
        <label className="bookings-search"><span className="bookings-search-label">Search equipment</span><input className="admin-native-input" type="search" value={search} onChange={event => onSearch(event.target.value)} placeholder="Asset, legacy label, model or serial" /></label>
      {canWrite && <Button type="button" className="primary bookings-add" onClick={() => setAdding(true)} icon={<svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" aria-hidden="true"><path d="M12 5v14M5 12h14" /></svg>}>Add unit</Button>}
      </div>
    </header>
    {adding && canWrite && <AddInventoryUnit onClose={() => setAdding(false)} onSaved={() => { setAdding(false); setMessage({ tone: "success", text: "Inventory unit added." }); onChanged() }} />}
  </>
}

function AddInventoryUnit({ onClose, onSaved }: { onClose: () => void; onSaved: () => void }) {
  const { data: products, loading, error: loadError, reload } = useLoad<Array<{ id: string; name: string }>>(() => supabase.from("products").select("id,name").order("name"))
  const [productId, setProductId] = React.useState("")
  const [assetCode, setAssetCode] = React.useState("")
  const [manualOnly, setManualOnly] = React.useState(false)
  const [busy, setBusy] = React.useState(false)
  const [error, setError] = React.useState("")
  const add = async (event: React.FormEvent) => {
    event.preventDefault()
    if (busy) return
    setBusy(true)
    setError("")
    try {
      const { error: rpcError } = await supabase.rpc("admin_add_inventory_unit", { p_product_id: productId, p_asset_code: assetCode, p_manual_only: manualOnly })
      if (rpcError) throw rpcError
      onSaved()
    } catch (error) {
      setError(error && typeof error === "object" && "message" in error ? String(error.message) : "The unit could not be added. Please try again.")
    } finally { setBusy(false) }
  }
  return <Dialog isOpen onOpenChange={open => { if (!open && !busy) onClose() }} purpose="form" width="min(560px, 96vw)" maxHeight="92dvh">
    <form className="ops-dialog" onSubmit={add}>
      <h2>Add unit</h2>
      {loading ? <SectionLoading /> : loadError ? <div className="message error" role="alert">{loadError}<Button type="button" onClick={reload}>Retry</Button></div> : <fieldset disabled={busy}>
        <AdminField>Product<select value={productId} onChange={e => setProductId(e.target.value)} required><option value="">Choose product</option>{(products ?? []).map(product => <option value={product.id} key={product.id}>{product.name}</option>)}</select></AdminField>
        <AdminField>Asset code<input value={assetCode} onChange={e => setAssetCode(e.target.value)} placeholder="Asset code" required /></AdminField>
        <AdminField>Booking access<select value={String(manualOnly)} onChange={e => setManualOnly(e.target.value === "true")}><option value="false">Online and manual</option><option value="true">Manual-only</option></select></AdminField>
      </fieldset>}
      {error && <p className="message error" role="alert">{error}</p>}
      <div className="ops-actions"><Button type="button" className="secondary" disabled={busy} onClick={onClose}>Cancel</Button><Button type="submit" className="primary" disabled={busy || loading || Boolean(loadError)}>{busy ? "Adding…" : "Add unit"}</Button></div>
    </form>
  </Dialog>
}

function DeliveryAreas({ setMessage }: { setMessage: (value: Message) => void }) {
  const { data, loading, error, reload } = useLoad<any[]>(() => supabase.from("delivery_postcode_rules").select("*").order("outward_code"))
  const { data: settings } = useLoad<any>(() => supabase.from("booking_settings").select("default_delivery_fee_pence").eq("id", 1).single())
  const [form, setForm] = React.useState({ code: "", enabled: true, feeMode: "default", customFee: "", note: "" })
  const [importFile, setImportFile] = React.useState<{ name: string; rules: DeliveryAreaImportRule[] } | null>(null)
  const [importErrors, setImportErrors] = React.useState<string[]>([])
  const [importing, setImporting] = React.useState(false)
  const fileInput = React.useRef<HTMLInputElement>(null)
  const save = async (event: React.FormEvent) => {
    event.preventDefault()
    const customFee = form.feeMode === "custom" ? parseDeliveryFeeGbp(form.customFee) : null
    if (form.feeMode === "custom" && (customFee === undefined || customFee === null || customFee === 0)) {
      setMessage({ tone: "error", text: "Enter a custom delivery fee greater than £0, or choose Free." })
      return
    }
    const { error: rpcError } = await supabase.rpc("admin_upsert_postcode_rule_with_fee", {
      p_outward_code: form.code,
      p_enabled: form.enabled,
      p_delivery_fee_override_pence: form.feeMode === "free" ? 0 : customFee,
      p_internal_note: form.note,
    })
    if (rpcError) setMessage({ tone: "error", text: rpcError.message })
    else {
      setForm({ code: "", enabled: true, feeMode: "default", customFee: "", note: "" })
      setMessage({ tone: "success", text: "Delivery area saved." })
      reload()
    }
  }
  const chooseImport = async (event: React.ChangeEvent<HTMLInputElement>) => {
    setImportFile(null); setImportErrors([])
    const file = event.target.files?.[0]
    if (!file) return
    if (file.size > MAX_DELIVERY_AREA_IMPORT_BYTES) {
      setImportErrors(["The CSV is larger than the 1 MB limit."])
      return
    }
    const result = parseDeliveryAreaCsv(await file.text())
    if (result.errors.length > 0) setImportErrors(result.errors)
    else setImportFile({ name: file.name, rules: result.rules })
  }
  const runImport = async () => {
    if (!importFile || importing) return
    setImporting(true); setMessage(null)
    const { data: result, error: rpcError } = await supabase.rpc("admin_bulk_upsert_postcode_rules", { p_rules: importFile.rules })
    setImporting(false)
    if (rpcError) { setImportErrors([rpcError.message]); return }
    const summary = result as { inserted?: number; updated?: number } | null
    setMessage({ tone: "success", text: `${summary?.inserted ?? 0} added, ${summary?.updated ?? 0} updated.` })
    setImportFile(null); setImportErrors([])
    if (fileInput.current) fileInput.current.value = ""
    reload()
  }
  const remove = async (code: string) => { if (!window.confirm(`Remove ${code} from configured delivery areas?`)) return; const { error: rpcError } = await supabase.rpc("admin_delete_postcode_rule", { p_outward_code: code }); if (rpcError) setMessage({tone:"error",text:rpcError.message}); else { setMessage({tone:"success",text:`${code} removed.`}); reload() } }
  if (loading) return <SectionLoading />
  if (error) return <Empty title="Delivery areas could not load" body={error} />
  const defaultFee = Number(settings?.default_delivery_fee_pence ?? 0)
  const feeLabel = (item: any) => !item.instant_checkout_enabled
    ? money.format(15)
    : item.delivery_fee_override_pence === null
    ? `Default (${money.format(defaultFee / 100)})`
    : Number(item.delivery_fee_override_pence) === 0
      ? "Free"
      : money.format(Number(item.delivery_fee_override_pence) / 100)
  return <div className="page-stack">
    <Panel title="Shared postcode tariff" subtitle="Used by jet washers. Dehumidifiers use their own tariff above; portable AC uses its fixed delivery charge. Exact first-chunk matching: SE23 and SW1A are separate rules.">
      <form className="override-form" onSubmit={save}>
        <AdminField>Outward code<input value={form.code} onChange={(event) => setForm({...form,code:event.target.value.toUpperCase().replace(/\s/g,"")})} placeholder="SE23" required /></AdminField>
        <Toggle label="Use configured area pricing" checked={form.enabled} onChange={(enabled) => setForm({...form,enabled})} />
        <AdminField>Delivery fee<select value={form.feeMode} onChange={(event) => setForm({...form,feeMode:event.target.value,customFee:""})}><option value="default">Default ({money.format(defaultFee / 100)})</option><option value="free">Free</option><option value="custom">Custom</option></select></AdminField>
        {form.feeMode === "custom" && <AdminField>Custom fee (£)<input inputMode="decimal" value={form.customFee} onChange={(event) => setForm({...form,customFee:event.target.value})} placeholder="7.99" required /></AdminField>}
        <AdminField className="wide">Internal note<input value={form.note} onChange={(event) => setForm({...form,note:event.target.value})} /></AdminField>
        <Button className="primary">Save area</Button>
      </form>
    </Panel>
    <Panel title="Bulk import delivery areas" subtitle="Existing codes are updated; codes missing from the file are left unchanged">
      <div className="csv-import">
        <code>outward_code,instant_checkout_enabled,delivery_fee_gbp</code>
        <p>Leave the fee blank for the default, use 0 for free delivery, or enter a GBP amount such as 7.99.</p>
        <AdminField className="csv-file">Choose CSV file<input ref={fileInput} type="file" accept=".csv,text/csv" onChange={chooseImport} /></AdminField>
        {importErrors.length > 0 && <div className="message error" role="alert"><strong>CSV could not be imported</strong><ul>{importErrors.slice(0, 20).map((item) => <li key={item}>{item}</li>)}</ul>{importErrors.length > 20 && <p>And {importErrors.length - 20} more errors.</p>}</div>}
        {importFile && <div className="csv-ready"><span><strong>{importFile.name}</strong><small>{importFile.rules.length} valid delivery areas</small></span><Button className="primary" disabled={importing} onClick={runImport}>{importing ? "Importing…" : `Import ${importFile.rules.length} areas`}</Button></div>}
      </div>
    </Panel>
    <Panel title="Configured delivery areas" subtitle={`${data?.length ?? 0} exact outward codes · all other postcodes use £15 delivery`}><Table headers={["Outward code","Area pricing","Delivery","Internal note","Action"]} rows={(data ?? []).map((item) => [<strong>{item.outward_code}</strong>,<Status value={item.instant_checkout_enabled ? "enabled" : "fallback £15"} />,feeLabel(item),item.internal_note || "—",<Button className="table-action" onClick={() => remove(item.outward_code)}>Remove</Button>])} /></Panel>
  </div>
}

function hireDays(deliveryDate: string, collectionDate: string) {
  const epochDay = (value: string) => {
    const [year, month, day] = value.split("-").map(Number)
    return Math.floor(Date.UTC(year, month - 1, day) / 86_400_000)
  }
  return Math.max(0, epochDay(collectionDate) - epochDay(deliveryDate) - 1)
}

function availabilityReason(value: string | null) {
  const labels: Record<string, string> = {
    NO_INVENTORY: "Not enough units",
    MINIMUM_NOTICE: "Inside booking notice",
    DELIVERY_DATE_BLOCKED: "Delivery date unavailable",
    COLLECTION_DATE_BLOCKED: "Collection date unavailable",
    DELIVERY_CAPACITY_EXHAUSTED: "Delivery date unavailable",
    COLLECTION_CAPACITY_EXHAUSTED: "Collection date unavailable",
  }
  return value ? labels[value] ?? value.toLowerCase().replaceAll("_", " ") : ""
}

function availabilityDelivery(item: AvailabilityCheck) {
  if (item.outcome === "contact_required") return "Contact required"
  if (item.outcome === "unavailable") return "Unavailable"
  if (item.free_delivery || item.delivery_fee_pence === 0) return "Free"
  return item.delivery_fee_pence === null ? "—" : money.format(item.delivery_fee_pence / 100)
}

function CheckResult({ item }: { item: AvailabilityCheck }) {
  if (item.outcome === "contact_required") return <div className="check-result"><Status value="contact_required" /><small>Delivery area needs confirmation</small></div>
  const detail = item.outcome === "unavailable"
    ? `${availabilityReason(item.reason_code)} · ${item.available_quantity} unit${item.available_quantity === 1 ? "" : "s"} available`
    : `${item.available_quantity} unit${item.available_quantity === 1 ? "" : "s"} available`
  return <div className="check-result"><Status value={item.outcome} /><small>{detail}</small></div>
}

function PostcodeChecks() {
  const [mapPostcode, setMapPostcode] = React.useState<string | null>(null)
  const mapTrigger = React.useRef<HTMLButtonElement | null>(null)
  const [page, setPage] = React.useState(1)
  const [groups, setGroups] = React.useState<AvailabilityCheckGroup[]>([])
  const [totalGroups, setTotalGroups] = React.useState(0)
  const [loading, setLoading] = React.useState(true)
  const [error, setError] = React.useState("")

  React.useEffect(() => {
    let active = true
    setLoading(true)
    setError("")
    Promise.resolve(supabase.rpc("admin_list_availability_check_groups", {
      p_page: page,
      p_page_size: ADMIN_PAGE_SIZE,
    })).then(({ data, error: loadError }) => {
        if (!active) return
        if (loadError) {
          setError(loadError.message)
          setGroups([])
          setTotalGroups(0)
        } else {
          const result = data as unknown as { groups?: AvailabilityCheckGroup[]; totalGroups?: number } | null
          setGroups(result?.groups ?? [])
          setTotalGroups(Number(result?.totalGroups ?? 0))
        }
      })
      .finally(() => active && setLoading(false))
    return () => { active = false }
  }, [page])

  if (loading) return <SectionLoading />
  if (error) return <Empty title="Postcode checks could not load" body={error} />

  const checked = (item: AvailabilityCheck) => <div><strong>{timeOnly.format(new Date(item.last_checked_at))}</strong>{item.check_count > 1 && <small>{item.check_count} checks since {timeOnly.format(new Date(item.first_checked_at))}</small>}{item.led_to_booking && <small className="booking-conversion">Confirmed booking</small>}</div>
  const checkedProduct = (item: AvailabilityCheck) => <div className="availability-product"><EquipmentStack productType={item.product_group} quantity={item.quantity} /><strong>{item.product_name || "Product unavailable"}</strong></div>
  const bookingValue = (item: AvailabilityCheck) => item.hire_pence == null ? "—" : money.format(item.hire_pence / 100)
  const dates = (item: AvailabilityCheck) => !item.delivery_date || !item.collection_date ? <span>Not selected</span> : <div><span>{dateOnly(item.delivery_date)} → {dateOnly(item.collection_date)}</span><small>{item.hire_days ?? hireDays(item.delivery_date, item.collection_date)}-day hire</small></div>

  return <div className="page-stack">
    <Panel title="Postcode checks" subtitle={`${totalGroups} postcode group${totalGroups === 1 ? "" : "s"} · newest first`}>
      <p className="check-stage-note">Checks cover the product, quantity and hire dates when selected. Contact-required postcodes also appear before dates are chosen. Booking value is equipment hire only, excluding delivery and accessories. A dash means no hire value was recorded.</p>
      {groups.length === 0 ? <div className="availability-empty"><strong>No postcode checks yet</strong><p>Valid Step 1 availability checks will appear here.</p></div> : <div className="postcode-groups">
        {groups.map((group) => <section className="postcode-group" key={group.postcode}>
          <header className="postcode-group-heading">
            <h3><button type="button" className="postcode-map-trigger" aria-label={`View ${group.postcode} on a map`} aria-haspopup="dialog" onClick={event => { mapTrigger.current = event.currentTarget; setMapPostcode(group.postcode) }}>{group.postcode}<svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" aria-hidden="true"><path d="M20 10c0 6-8 12-8 12S4 16 4 10a8 8 0 1 1 16 0Z" /><circle cx="12" cy="10" r="2.5" /></svg></button></h3>
            <span>{group.check_count} check{group.check_count === 1 ? "" : "s"}</span>
          </header>
          {groupByCreatedDate(group.checks, (item) => item.last_checked_at).map((dateGroup) => <div className="dated-checks" key={dateGroup.key}>
            <h4>{dateGroup.label}</h4>
            <div className="availability-table">
              <Table headers={["Time","Product","Hire dates","Result","Booking value","Delivery"]} rows={dateGroup.items.map((item) => [checked(item),checkedProduct(item),dates(item),<CheckResult item={item} />,bookingValue(item),availabilityDelivery(item)])} rowClassNames={dateGroup.items.map((item) => item.led_to_booking ? "availability-converted" : undefined)} />
            </div>
            <div className="availability-cards">
              {dateGroup.items.map((item) => <article className={`availability-card condensed${item.led_to_booking ? " availability-converted" : ""}`} key={item.id}>
                <header>{checked(item)}<CheckResult item={item} /></header>
                <dl>
                  <div><dt>Product</dt><dd>{checkedProduct(item)}</dd></div>
                  <div><dt>Hire dates</dt><dd>{dates(item)}</dd></div>
                  <div><dt>Booking value</dt><dd>{bookingValue(item)}</dd></div>
                  <div><dt>Delivery</dt><dd>{availabilityDelivery(item)}</dd></div>
                </dl>
              </article>)}
            </div>
          </div>)}
        </section>)}
      </div>}
      <Pagination page={page} total={totalGroups} pageSize={ADMIN_PAGE_SIZE} label="postcode groups" onChange={setPage} />
      {mapPostcode && <PostcodeMapDialog key={mapPostcode} postcode={mapPostcode} onClose={() => { setMapPostcode(null); requestAnimationFrame(() => mapTrigger.current?.focus({ preventScroll: true })) }} />}
    </Panel>
  </div>
}

function Panel({ title, subtitle, children }: { title: string; subtitle?: string; children: React.ReactNode }) { return <section className="panel"><div className="panel-heading"><div><h2>{title}</h2>{subtitle && <p>{subtitle}</p>}</div></div>{children}</section> }
function Table({ headers, rows, rowClassNames, className = "", onRowClick }: {
  headers: string[]; rows: React.ReactNode[][]; rowClassNames?: Array<string | undefined>; className?: string; onRowClick?: (index: number) => void
}) {
  return <div className={`table-scroll ${className}`.trim()}><AstryxTable>
    <TableHeader><TableRow>{headers.map((item, index) => <TableHeaderCell key={index}>{item}</TableHeaderCell>)}</TableRow></TableHeader>
    <TableBody>{rows.length ? rows.map((row, index) => <TableRow className={rowClassNames?.[index]} key={index} data-clickable-row={onRowClick ? true : undefined} onClick={onRowClick ? event => {
      if (event.defaultPrevented || (event.target as HTMLElement).closest('button, a, input, select, textarea, [role="button"]')) return
      onRowClick(index)
    } : undefined}>{row.map((cell, cellIndex) => <TableCell key={cellIndex}>{cell}</TableCell>)}</TableRow>) : <TableRow><TableCell colSpan={headers.length}>Nothing to show yet.</TableCell></TableRow>}</TableBody>
  </AstryxTable></div>
}
function Pagination({ page, total, pageSize, label, onChange }: { page: number; total: number; pageSize: number; label: string; onChange: (page: number) => void }) { const totalPages = Math.max(1, Math.ceil(total / pageSize)); return <nav className="pagination" aria-label={`${label} pages`}><Button type="button" className="secondary" disabled={page <= 1} onClick={() => onChange(Math.max(1, page - 1))}>Previous</Button><span>Page {page} of {totalPages}<small>{total} {label}</small></span><Button type="button" className="secondary" disabled={page >= totalPages} onClick={() => onChange(Math.min(totalPages, page + 1))}>Next</Button></nav> }
function Toggle({ label, checked, onChange }: { label: string; checked: boolean; onChange: (value: boolean) => void }) { return <Switch label={label} value={checked} onChange={onChange} /> }
function Status({ value }: { value: string }) { return <Badge className={`status status-${value.replaceAll("_","-")}`} label={value.replaceAll("_"," ")} /> }

function Empty({ title, body }: { title: string; body: string }) { return <div className="panel empty"><h2>{title}</h2><p>{body}</p></div> }
function SectionLoading() { return <div className="section-loading"><span /> Loading…</div> }
function title(value: string) { return value.charAt(0).toUpperCase() + value.slice(1) }
function NavIcon({ name }: { name: Tab }) {
  if (name === "settings") return <svg width="17" height="17" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"><path d="m9 3-.5 2.2-1.7 1L4.6 6 2.5 9.5 4 11v2l-1.5 1.5L4.6 18l2.2-.2 1.7 1L9 21h6l.5-2.2 1.7-1 2.2.2 2.1-3.5L20 13v-2l1.5-1.5L19.4 6l-2.2.2-1.7-1L15 3Z" /><circle cx="12" cy="12" r="3" /></svg>
  const icons = { customers:"♙", fleet:"▤", analytics:"↗", dashboard:"⌂", inventory:"▦", bookings:"≡" }
  return <span aria-hidden="true">{icons[name]}</span>
}
function BinIcon() { return <svg viewBox="0 0 24 24" aria-hidden="true"><path d="M4 7h16M9 7V4h6v3m3 0-1 13H7L6 7m4 4v5m4-5v5" /></svg> }
